import { PortalVerticalGameTilePropTypes } from './../../types/gameTypes';
import {
  AlgoliaFactoryConfig,
  AlgoliaInterface,
  GameFromAlgolia,
} from './types';
import algolia from 'algoliasearch/dist/algoliasearch-lite.esm.browser.js';
import { mapAlgoliaGame, filterInvalidGames, convertToObject } from './utils';
import { nanoid } from 'nanoid';
import { GAME_LAUNCH_PLACEMENT } from './constants';

export const _mocks: { client: any } = {
  client: null,
};

const AlgoliaFactory = ({
  appId,
  apiKey,
  index,
}: AlgoliaFactoryConfig): AlgoliaInterface => {
  // @ts-ignore
  const client = _mocks.client ? _mocks.client() : algolia(appId, apiKey);
  const idx = client.initIndex(index);
  //search-insights requires userToken, but we do not need it for tracking purposes - that is why we are using id generator per session.
  const userToken = nanoid();

  return {
    async searchItems(
      query: string,
      filters: string[],
      locale?: string,
      base_path?: string
    ) {
      const { default: searchInsights } = await import(
        /* webpackChunkName: "search-insights" */ 'search-insights'
      );
      searchInsights.init({
        appId,
        apiKey,
      });
      const MAX_SEARCH_ITEMS_AMOUNT = 50;
      const mappedFilters = filters
        .map(val => `"searchMarkets":"${val}"`)
        .join(' OR ');

      try {
        const results = await idx.search(query, {
          filters: mappedFilters,
          hitsPerPage: MAX_SEARCH_ITEMS_AMOUNT,
          clickAnalytics: true,
        });

        const items = filterInvalidGames(results.hits).map(game =>
          mapAlgoliaGame({
            game,
            locale,
            base_path,
            game_launch_placement: GAME_LAUNCH_PLACEMENT,
          })
        );

        return {
          items: items,
          queryID: results.queryID,
          userToken,
        };
      } catch (e) {
        console.error(e);
        return e;
      }
    },

    async fetchMatchingGames({
      filters,
      amount,
      onlyIds,
      locale,
    }: {
      filters: { name: string; values: string[] }[];
      amount?: number;
      onlyIds?: boolean;
      locale?: string;
    }) {
      if (!filters.length) return [];
      let maxAmount = 0;

      try {
        if (!amount) {
          const filtersWithAmount = await idx
            .search('', {
              facets: filters.map(f => f.name),
              analytics: false,
              hitsPerPage: 0,
            })
            .then(({ facets }: { [key: string]: number }) => facets);
          filters.forEach(f => {
            f.values.forEach(v => {
              maxAmount =
                maxAmount + filtersWithAmount[f.name]
                  ? filtersWithAmount[f.name][v]
                  : 0;
            });
          });
        }

        const fetchedGames = await idx
          .search('', {
            hitsPerPage: amount || maxAmount,
            filters: filters
              .map(filter =>
                filter.values
                  .map(value => `${filter.name}:"${value}"`)
                  .join(' AND ')
              )
              .join(' AND '),
            analytics: false,
            attributesToRetrieve: onlyIds ? ['uid'] : ['*'],
          })
          .then(
            ({ hits }: { hits: Array<{ uid: string }> }) =>
              hits as GameFromAlgolia[]
          );

        if (onlyIds)
          return {
            gameIds: fetchedGames.map((game: GameFromAlgolia) => game.uid),
            games: [],
            gamesMap: {},
          };

        const filtered = filterInvalidGames(fetchedGames);

        const mappedGames = filtered.map((game: GameFromAlgolia) =>
          mapAlgoliaGame({
            game,
            locale,
            game_launch_placement: GAME_LAUNCH_PLACEMENT,
          })
        );
        return {
          games: mappedGames,
          gamesMap: convertToObject<PortalVerticalGameTilePropTypes>(
            mappedGames,
            filtered.map(game => game.uid)
          ),
        };
      } catch (e) {
        console.error(e);
        return e;
      }
    },
  };
};

export default AlgoliaFactory;
